<template>
  <popup-dialog
    class="net-layers-popup"
    :width="width"
    :height="height"
    :min-width="200"
    :backdrop="false"
    :resizable="true"
    x-alignment="right"
    y-alignment="bottom"
    header-class="net-layers-popup__header"
    @move="emit('move', $event)"
    @resize="emit('resize', $event)"
    @close="emit('close')"
  >
    <template #header="{ close }">
      <h3 class="net-layers-popup__title">{{ $t("objectNames.layer", 4) }}</h3>
      <button type="button" class="button-icon" @click.stop="close()">
        <LucideIcon name="x" />
      </button>
    </template>
    <template #default>
      <div class="net-layers-popup__content">
        <SceneLayersMenu :editable="editable" />
      </div>
      <div v-if="editable">
        <button
          class="button-icon"
          type="button"
          :title="$t('actions.createLayer')"
          @click="createSceneLayer()"
        >
          <lucide-icon name="layers-plus" />
        </button>
      </div>
    </template>
  </popup-dialog>
</template>

<script setup lang="ts">
import SceneLayersMenu from './SceneLayersMenu.vue';
import PopupDialog from '@core/ui/PopupDialog.vue';
import LucideIcon from '@core/ui/LucideIcon.vue';
import { useSceneLayerStore } from '../sceneLayer/sceneLayers.store';
import type { Uid } from '@/helpers/utility/Id';
import { usePresenceStore } from '@editor/store/presence.store';

withDefaults(defineProps<{
  editable: boolean
  width?: number | null,
  height?: number | null
}>(), {
  width: 300
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'move', position: { x: number; y: number; width?: number; height?: number }): void;
  (e: 'resize', size: { x: number; y: number; width: number; height: number }): void;
}>();

const sceneLayerStore = useSceneLayerStore();
const presence = usePresenceStore();

async function createSceneLayer() {
  const newLayer = await sceneLayerStore.create({});
  selectSceneLayer(newLayer.uid);
}

function selectSceneLayer(uid: Uid<"sceneNode">) {
  presence.setActiveLayer(uid);
}
</script>

<style lang="scss">
.net-layers-popup {
  user-select: none;
  max-height: calc(100vh - 6rem);
  display: grid;
  grid-template-rows: min-content minmax(0, 1fr);

  &__header {
    padding: 0.25rem 0.5rem;
    padding-left: 0.5rem;
    background-color: var(--color-background);
    display: grid;
    grid-template-columns: 1fr min-content;
  }

  &__title {
    font-size: 1em;
    font-weight: bold;
    margin: 0;
    align-self: center;
  }

  &__content {
    overflow: auto;
    padding: 0.5rem 0.25rem;
  }
}
</style>